<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  campaign: {
    type: Object,
    default: null,
  },
  fetchLogs: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['close']);

const selectedDate = ref(new Date().toISOString().split('T')[0]);
const logsData = ref(null);
const loading = ref(false);

const loadLogs = async () => {
  if (!props.campaign) return;
  loading.value = true;
  logsData.value = await props.fetchLogs(props.campaign.id, {
    date: selectedDate.value,
  });
  loading.value = false;
};

watch(() => props.isOpen, (newVal) => {
  if (newVal && props.campaign) {
    selectedDate.value = new Date().toISOString().split('T')[0];
    loadLogs();
  }
});

const statusColors = {
  sent: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  skipped: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const chartData = computed(() => {
  if (!logsData.value?.daily_stats) return [];
  const stats = logsData.value.daily_stats;
  const dates = Object.keys(stats).sort();
  const maxSent = Math.max(
    ...dates.map(d => (stats[d]?.sent || 0) + (stats[d]?.failed || 0) + (stats[d]?.skipped || 0)),
    1
  );
  return dates.map(date => ({
    date,
    sent: stats[date]?.sent || 0,
    failed: stats[date]?.failed || 0,
    skipped: stats[date]?.skipped || 0,
    total: (stats[date]?.sent || 0) + (stats[date]?.failed || 0) + (stats[date]?.skipped || 0),
    height: ((stats[date]?.sent || 0) + (stats[date]?.failed || 0) + (stats[date]?.skipped || 0)) / maxSent * 100,
  }));
});

const formatTime = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div class="bg-n-solid-2 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('OMNI.WA_CAMPAIGNS.LOGS.TITLE') }} - {{ campaign.title }}
          </h2>
        </div>
        <button class="text-n-slate-10 hover:text-n-slate-12" @click="emit('close')">
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Date Filter + Summary -->
      <div class="px-6 py-4 border-b border-n-weak">
        <div class="flex items-center gap-4">
          <div>
            <label class="block text-xs text-n-slate-10 mb-1">{{ t('OMNI.WA_CAMPAIGNS.LOGS.DATE_LABEL') }}</label>
            <input
              v-model="selectedDate"
              type="date"
              class="px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 text-sm"
              @change="loadLogs"
            />
          </div>
          <div v-if="logsData?.summary" class="flex gap-4 ml-auto">
            <div class="text-center">
              <div class="text-lg font-bold text-green-600 dark:text-green-400">
                {{ logsData.summary.sent }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('OMNI.WA_CAMPAIGNS.LOGS.SENT') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-red-600 dark:text-red-400">
                {{ logsData.summary.failed }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('OMNI.WA_CAMPAIGNS.LOGS.FAILED') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                {{ logsData.summary.skipped }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('OMNI.WA_CAMPAIGNS.LOGS.SKIPPED') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-if="loading" class="flex items-center justify-center h-32">
          <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-woot-500" />
        </div>

        <template v-else-if="logsData">
          <!-- Daily Chart -->
          <div v-if="chartData.length > 0" class="mb-6">
            <h3 class="text-sm font-medium text-n-slate-11 mb-3">
              {{ t('OMNI.WA_CAMPAIGNS.LOGS.DAILY_CHART') }}
            </h3>
            <div class="flex items-end gap-1 h-24">
              <div
                v-for="bar in chartData"
                :key="bar.date"
                class="flex-1 flex flex-col items-center"
              >
                <div
                  class="w-full bg-green-400 dark:bg-green-500 rounded-t"
                  :style="{ height: `${bar.height}%`, minHeight: bar.total > 0 ? '4px' : '0' }"
                  :title="`${bar.date}: ${bar.sent} sent, ${bar.failed} failed, ${bar.skipped} skipped`"
                />
              </div>
            </div>
            <div class="flex justify-between text-[10px] text-n-slate-10 mt-1">
              <span v-if="chartData.length > 0">{{ chartData[0].date }}</span>
              <span v-if="chartData.length > 1">{{ chartData[chartData.length - 1].date }}</span>
            </div>
          </div>

          <!-- Logs Table -->
          <div v-if="logsData.logs && logsData.logs.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-10">
                    {{ t('OMNI.WA_CAMPAIGNS.LOGS.CONTACT') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-10">
                    {{ t('OMNI.WA_CAMPAIGNS.LOGS.PHONE') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-10">
                    {{ t('OMNI.WA_CAMPAIGNS.LOGS.STATUS') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-10">
                    {{ t('OMNI.WA_CAMPAIGNS.LOGS.SENT_AT') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-10">
                    {{ t('OMNI.WA_CAMPAIGNS.LOGS.ERROR') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="log in logsData.logs"
                  :key="log.id"
                  class="border-b border-n-weak/50 hover:bg-n-alpha-3"
                >
                  <td class="py-2 px-3 text-n-slate-12">
                    {{ log.contact_name }}
                  </td>
                  <td class="py-2 px-3 text-n-slate-11 font-mono text-xs">
                    {{ log.contact_phone }}
                  </td>
                  <td class="py-2 px-3">
                    <span
                      class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="statusColors[log.status]"
                    >
                      {{ log.status }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-n-slate-11 text-xs">
                    {{ formatTime(log.sent_at) }}
                  </td>
                  <td class="py-2 px-3 text-red-500 text-xs truncate max-w-48">
                    {{ log.error_message }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="text-center py-8 text-n-slate-10">
            {{ t('OMNI.WA_CAMPAIGNS.LOGS.NO_LOGS') }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
